import React from 'react';
import { css } from '@emotion/react';
import { StaticImage } from 'gatsby-plugin-image';
import ResponderLogoIcon from '../../images/first-responder-logo.svg';
import Container from '../primitives/grid/container';
import { colors } from '../primitives/colors';
import RichTextEditorStyles from '../utils/rich-text-editor-styles';
import Link from '../primitives/link';
import IconFacebook from '../../images/svg/facebook.svg';
import IconLinkedIn from '../../images/svg/linkedin.svg';
import IconTwitter from '../../images/svg/twitter.svg';
import IconYoutube from '../../images/svg/youtube.svg';
import {
    SOCIAL_URL_FACEBOOK,
    SOCIAL_URL_LINKEDIN,
    SOCIAL_URL_TWITTER,
    SOCIAL_URL_YOUTUBE,
} from '../../constants';
import FranchiseLicenses from '../franchise/microsite/licenses';
import Row from '../primitives/grid/row';
import Col from '../primitives/grid/col';
import { breakpoints } from '../primitives/tokens';

// Shared styles that both footer variants use
export const sharedFooterStyles = {
    autoPilot: css({
        backgroundColor: colors.primaryOrange,
        '& .be-ix-link-block': {
            display: 'flex',
            justifyContent: 'end',
            color: colors.shadesWhite,
            '.be-related-link-container': {
                padding: '24px 0',
                display: 'flex',
                flexWrap: 'wrap',
                flexDirection: 'column',
                textAlign: 'right',
                '@media (min-width: 768px)': {
                    padding: '24px 0',
                    gap: '6px 24px',
                    flexDirection: 'row',
                },
                '@media (min-width: 1024px)': {
                    padding: '16px 0',
                },
                '.be-label': {
                    fontWeight: '600',
                    flexBasis: 'auto',
                    marginBottom: '4px',
                    '@media (min-width: 768px)': {
                        gap: '16px',
                        marginBottom: 0,
                    },
                },
                '.be-list': {
                    display: 'flex',
                    flexDirection: 'column',
                    flexWrap: 'wrap',
                    '@media (min-width: 768px)': {
                        gap: '0 16px',
                        flexDirection: 'row',
                    },
                },
            },
        },
    }),
    cursorDefault: css({
        cursor: 'default',
    }),
    siteFooter: css({
        overflow: 'hidden',
        position: 'relative',
        zIndex: 10,
        backgroundColor: colors.shadesWhite,
        borderTopWidth: '2px',
        borderColor: colors.primaryOrange,
        [breakpoints.mb]: {
            marginBottom: '60px',
        },
    }),
    widgetSection: css({
        position: 'relative',
        paddingTop: '36px',
        paddingBottom: '72px',
        [breakpoints.tb]: {
            paddingBottom: '0',
        },
        '&.microsite': {
            paddingTop: '0',
        },
    }),
    staticDisclaimer: css({
        marginBottom: '24px',
        lineHeight: 1.375,
    }),
    dynamicDisclaimersWrap: css({
        fontSize: '14px',
        lineHeight: '20px',
    }),
    subfooter: css({
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderTopWidth: '1px',
        fontSize: '14px',
        lineHeight: '20px',
        borderColor: colors.grey300,
        paddingTop: '12px',
        paddingBottom: '12px',
        [breakpoints.mb]: {
            flexDirection: 'column',
            gap: '12px',
            fontSize: '18px',
            lineHeight: '28px',
            border: 'none',
        },
    }),
    subfooterCol: css({
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        width: '100%',
        flex: '1 1 auto',
        [breakpoints.mb]: {
            justifyContent: 'space-between',
        },
    }),
    colophon: css({
        paddingTop: '16px',
        paddingBottom: '16px',
        textAlign: 'left',
        width: 'auto',
        [breakpoints.mb]: {
            width: '100%',
            textAlign: 'center',
            paddingTop: '0',
        },
    }),
    navImageOuterWrap: css({
        display: 'flex',
        right: '0',
        bottom: '0',
        left: '0',
        paddingTop: '8px',
        paddingBottom: '8px',
    }),
    navImageInnerWrap: css({
        justifyContent: 'flex-end',
        paddingBottom: '12px',
        marginTop: '12px',
        flexDirection: 'row',
        '>:not([hidden])~:not([hidden])': {
            marginLeft: '32px',
        },
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        [breakpoints.tb]: {
            marginTop: '32px',
            paddingBottom: '32px',
            justifyContent: 'flex-start',
        },
        [breakpoints.mb]: {
            paddingBottom: '12px',
            marginTop: '12px',
            gap: '16px',
            flexDirection: 'column',
        },
    }),
    navTitle: css({
        paddingBottom: '4px',
        marginBottom: '8px',
        maxWidth: '160px',
        width: '100%',
        lineHeight: '28px',
        fontWeight: 500,
        color: colors.primaryOrange,
        borderBottomWidth: '1px',
        borderColor: colors.primaryOrange,
        fontSize: '21px',
        [breakpoints.mb]: {
            fontSize: '18px',
            maxWidth: 'unset',
        },
    }),
    navContainer: css({
        display: 'flex',
    }),
    navLinks: (doubleWidth: boolean) => css({
        display: 'grid',
        gridTemplateColumns: doubleWidth ? 'repeat(2, minmax(0, 1fr))' : 'repeat(1, minmax(0, 1fr))',
        columnGap: !doubleWidth ? '0' : '24px',
        marginRight: '12px',
        gridAutoFlow: 'column',
        gridTemplateRows: 'repeat(13, min-content)',
        width: '100%',
        [breakpoints.tb]: {
            display: 'flex',
            flexDirection: 'column',
        },
    }),
    navLinksItem: css({
        marginBottom: '12px',
        '.nav-links': {
            background: 'red',
            paddingLeft: '12px',
        },
    }),
    franchiseAddress: css({
        fontSize: '16px',
        lineHeight: '24px',
        marginTop: '0',
        color: colors.grey700,
    }),
    socialMediaLinks: css({
        display: 'flex',
        alignItems: 'center',
        gap: '24px',
        marginLeft: 'auto',
        maxWidth: 'max-content',
        '>:not([hidden])~:not([hidden])': {
            marginLeft: '32px',
        },
        [breakpoints.mb]: {
            justifyContent: 'space-between',
            marginLeft: 'unset',
            width: '100%',
            maxWidth: 'unset',
        },
    }),
    socialMediaLink: css({
        color: colors.primaryOrange,
        svg: {
            fill: colors.primaryOrange,
            width: '21px',
            height: '21px',
        },
        '&:hover': {
            opacity: 0.8,
        },
    }),
    licenseSection: css({
        marginTop: '20px',
        paddingTop: '20px',
        borderTop: '1px solid',
        borderColor: colors.grey300,
    }),
};

type Sublink = { title?: string, url?: string, className?: string, sublinks?: Sublink[] };

export interface FooterBaseProps {
    isMicrosite: boolean;
    links: Array<{
        title?: string | null;
        links?: Array<{
            title?: string | null;
            url?: string | null;
            className?: string | null;
            sublinks?: Array<{
                title?: string | null;
                url?: string | null;
                className?: string | null;
                sublinks?: Array<{
                    title?: string | null;
                    url?: string | null;
                }>;
            }>;
        }> | null;
    }> | null;
    staticDisclaimerCopy?: string | null;
    dynamicFooterDisclaimers: ReadonlyArray<{
        contentBefore?: string | null;
        content?: string | null;
        pageMatch?: string | null;
    }>;
    currentPath?: string;
    children?: React.ReactNode;
    franchise?: {
        name?: string | null;
        addressHidden?: boolean | null;
        address?: {
            address?: string | null;
            addressExt?: string | null;
            city?: string | null;
            state?: string | null;
            postalCode?: string | null;
        } | null;
        socialMedia?: {
            linkedIn?: string | null;
            twitter?: string | null;
            instagram?: string | null;
            facebook?: string | null;
        } | null;
    } | null;
}

const FooterBase: React.FC<FooterBaseProps> = ({
    isMicrosite,
    links,
    staticDisclaimerCopy,
    dynamicFooterDisclaimers,
    currentPath,
    children,
    franchise,
}) => (
    <footer css={sharedFooterStyles.siteFooter}>
        {!isMicrosite && (
            <div css={sharedFooterStyles.autoPilot}>
                <Container>
                    <div className="be-ix-link-block" />
                </Container>
            </div>
        )}
        <Container>
            <section css={sharedFooterStyles.widgetSection} id="main-footer--nav" className={isMicrosite ? 'microsite' : ''}>
                {children}
                <Row css={{
                    rowGap: '24px',
                    gridTemplateRows: isMicrosite ? 'unset' : 'repeat(2, 1fr)',
                    [breakpoints.mb]: {
                        gridTemplateRows: 'unset',
                    },
                }}
                >
                    {links?.map((group, id) => {
                        if (!group) return null;
                        const doubleWidth = !isMicrosite && id === 0;

                        const isHalfHeightAtDt = !isMicrosite && id >= links.length - 2;
                        const isHalfHeightAtTb = !isMicrosite && id !== 0;

                        if (!group.links || group.links.filter(x => !!x).length === 0) return null;

                        return (
                            <Col
                                key={group.title}
                                breakpoints={{
                                    dt: isMicrosite ? { span: 2 } : { span: id === 0 ? 6 : 2 },
                                    tb: { span: 4 },
                                }}
                                css={{
                                    gridRow: isMicrosite ? undefined : `span ${isHalfHeightAtDt ? 1 : 2}`,
                                    [breakpoints.tb]: {
                                        gridRow: isMicrosite ? undefined : `span ${isHalfHeightAtTb ? 1 : 2}`,
                                    },
                                    [breakpoints.mb]: {
                                        gridRow: isMicrosite ? undefined : 'unset',
                                    },
                                }}
                            >
                                <p css={sharedFooterStyles.navTitle}>{group.title}</p>
                                <div css={sharedFooterStyles.navContainer}>
                                    <ul css={sharedFooterStyles.navLinks(doubleWidth)}>
                                        {group.links.map((groupLink) => {
                                            if (!groupLink) return null;

                                            const { title, url } = groupLink;
                                            const sublinks = (('sublinks' in groupLink && Array.isArray(groupLink.sublinks)) ? groupLink?.sublinks : []) as Sublink[];
                                            const className = groupLink?.className || '';

                                            return (
                                                <React.Fragment key={title}>
                                                    <li css={sharedFooterStyles.navLinksItem}>
                                                        {url ? (
                                                            <Link to={url} className={className}>
                                                                {title}
                                                            </Link>
                                                        ) : (
                                                            <p css={sharedFooterStyles.cursorDefault} className={className}>
                                                                {title}
                                                            </p>
                                                        )}
                                                    </li>
                                                    {sublinks.length > 0 && sublinks.map((sublink) => {
                                                        const {
                                                            title: sublinkTitle,
                                                            url: sublinkUrl,
                                                        } = sublink;

                                                        const nestedSublinks = sublink?.sublinks || [];
                                                        const subClassName = sublink.className;

                                                        return (
                                                            <React.Fragment key={sublinkTitle}>
                                                                <li css={[sharedFooterStyles.navLinksItem, { '@media (min-width: 375px)': { marginLeft: '12px' } }]}>
                                                                    {sublinkUrl ? (
                                                                        <Link to={sublinkUrl} className={subClassName}>
                                                                            {sublinkTitle}
                                                                        </Link>
                                                                    ) : (
                                                                        <p
                                                                            css={sharedFooterStyles.cursorDefault}
                                                                            className={subClassName}
                                                                        >
                                                                            {sublinkTitle}
                                                                        </p>
                                                                    )}
                                                                </li>
                                                                {nestedSublinks.length > 0 && nestedSublinks.map(
                                                                    (nestedSubLink) => {
                                                                        const {
                                                                            title: nestedSublinkTitle,
                                                                            url: nestedSublinkUrl,
                                                                        } = nestedSubLink;

                                                                        const nestedClassName = nestedSubLink.className;

                                                                        return (
                                                                            <li
                                                                                css={[sharedFooterStyles.navLinksItem, { '@media (min-width: 375px)': { marginLeft: '8px' } }]}
                                                                                key={nestedSublinkTitle}
                                                                            >
                                                                                {nestedSublinkUrl ? (
                                                                                    <Link
                                                                                        to={nestedSublinkUrl}
                                                                                        className={nestedClassName}
                                                                                    >
                                                                                        {nestedSublinkTitle}
                                                                                    </Link>
                                                                                ) : (
                                                                                    <p
                                                                                        css={sharedFooterStyles.cursorDefault}
                                                                                        className={nestedClassName}
                                                                                    >
                                                                                        {nestedSublinkTitle}
                                                                                    </p>
                                                                                )}
                                                                            </li>
                                                                        );
                                                                    },
                                                                )}
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </React.Fragment>
                                            );
                                        })}
                                    </ul>
                                </div>
                            </Col>
                        );
                    })}

                    {/* Franchise licenses as a separate column */}
                    {isMicrosite && franchise && (
                        <Col
                            breakpoints={{
                                dt: { span: 3 },
                                tb: { span: 6 },
                            }}
                        >
                            <FranchiseLicenses />
                        </Col>
                    )}

                    {/* Franchise address as the last column in the grid */}
                    {isMicrosite && franchise && franchise.name && !franchise.addressHidden && franchise.address && (
                        <Col
                            breakpoints={{
                                dt: { span: 3 },
                                tb: { span: 6 },
                            }}
                        >
                            <address css={sharedFooterStyles.franchiseAddress}>
                                <strong>{franchise.name}</strong>
                                <br />
                                {!!franchise.address?.address && (
                                    <>
                                        {franchise.address.address}
                                        <br />
                                    </>
                                )}
                                {!!franchise.address?.addressExt && (
                                    <>
                                        {franchise.address.addressExt}
                                        <br />
                                    </>
                                )}
                                {franchise.address?.city}
                                {', '}
                                {franchise.address?.state}
                                {' '}
                                {franchise.address?.postalCode}
                            </address>
                        </Col>
                    )}
                </Row>

                <div css={sharedFooterStyles.navImageOuterWrap}>
                    <div css={sharedFooterStyles.navImageInnerWrap}>
                        <StaticImage
                            width={300}
                            alt="Certificates"
                            formats={['auto', 'webp']}
                            src="../../images/certificates.png"
                        />
                        <ResponderLogoIcon />
                    </div>
                </div>
            </section>

            {!!staticDisclaimerCopy && (
                <section css={sharedFooterStyles.dynamicDisclaimersWrap}>
                    <div
                        css={[RichTextEditorStyles, sharedFooterStyles.staticDisclaimer]}
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: staticDisclaimerCopy || '' }}
                    />
                </section>
            )}

            <section css={sharedFooterStyles.dynamicDisclaimersWrap}>
                {dynamicFooterDisclaimers
                    .filter(fd => fd?.pageMatch === currentPath)
                    .map(fd => (
                        <React.Fragment key={fd.content}>
                            {!!fd?.contentBefore && (
                                <div
                                    css={[RichTextEditorStyles, sharedFooterStyles.staticDisclaimer]}
                                    // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{ __html: fd.contentBefore || '' }}
                                />
                            )}
                            {!!fd?.content && (
                                <div
                                    css={[RichTextEditorStyles, sharedFooterStyles.staticDisclaimer]}
                                    // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{ __html: fd.content || '' }}
                                />
                            )}
                        </React.Fragment>
                    ))}
            </section>

            <section css={sharedFooterStyles.subfooter} id="main-footer--subfooter">
                <div css={sharedFooterStyles.subfooterCol}>
                    <p css={sharedFooterStyles.colophon}>
                        ©
                        {' '}
                        {new Date().getFullYear()}
                        {' '}
                        SERVPRO. All rights reserved.
                    </p>
                </div>
                {!isMicrosite && (
                    <div css={sharedFooterStyles.subfooterCol}>
                        <ul css={sharedFooterStyles.socialMediaLinks}>
                            <Link css={sharedFooterStyles.socialMediaLink} to={SOCIAL_URL_LINKEDIN} target="_blank">
                                <IconLinkedIn />
                            </Link>
                            <Link css={sharedFooterStyles.socialMediaLink} to={SOCIAL_URL_TWITTER} target="_blank">
                                <IconTwitter />
                            </Link>
                            <Link css={sharedFooterStyles.socialMediaLink} to={SOCIAL_URL_YOUTUBE} target="_blank">
                                <IconYoutube />
                            </Link>
                            <Link css={sharedFooterStyles.socialMediaLink} to={SOCIAL_URL_FACEBOOK} target="_blank">
                                <IconFacebook />
                            </Link>
                        </ul>
                    </div>
                )}
            </section>
        </Container>
    </footer>
);

export default FooterBase;
